// EigenverbruikDuoTile — Dashboard cycle #50 (Bram feedback 2026-05-22).
//
// Bidirectionele "Eigenverbruik PV" tegel — vervangt de oude single-pct
// KpiTile die te weinig info gaf (Bram: "grote tegel, te weinig info").
//
// Twee getallen prominent naast elkaar:
//   - Links:  PV-dekking      = pvToLoadKwh / load × 100   (% van verbruik door PV)
//   - Rechts: PV-zelfverbruik = pvToLoadKwh / PV_totaal × 100 (% van PV blijft thuis)
//
// Belangrijk: we kunnen NIET pvToLoadKwh berekenen uit grid_export alleen, want
// op een battery-site bevat grid_export óók battery→grid (overnight arbitrage).
// Een correcte attribution vraagt per-tick flow-tracking dat we niet hebben.
//
// Bram-quote (2026-05-22): "Hoeveel van ons eigenverbruik is vandaag (zowel
// load als PV tot op heden vanaf middernacht) gedekt uit PV en andersom:
// hoeveel van onze PV hebben we zelfverbruikt en hoeveel surplus verkopen we?"
//
// Daghoeveelheden:
//   PV_total = wat de PV-strings hebben opgewekt (snapshot: _pv_total_today_kwh)
//   Load_total = household_load + backup_load (snapshot: dagteller)
//   pvToLoad  = Σ min(pv, last) per 5-min bucket sinds NL-middernacht
//               (directPvToLoadTodayKwh), begrensd op PV_total en Load_total
//   PV_surplus = max(0, PV_total - pvToLoad)  (naar accu of net)
//
// Wachtrij #492: tot 14-10-2026 was pvToLoad = min(PV_total, Load_total). Dan
// staat altijd één van de twee percentages per constructie op 100%, en de
// ander 2-4× te hoog (VJ 07-13 okt: zelfverbruik elke dag 100% waar 26-91%
// klopte). Zonder historie van vandaag toont de tegel "—", geen benadering.
//
// Mobiel (panel-kpi flex-column): tegel spant volle breedte via .kpi-tile-double.
// Beide percentages staan side-by-side in een 2-koloms flex-row. kWh-waarden
// staan secundair eronder. Visuele bar onder elk getal toont de ratio (0..100).

import './kpi.css';
import type { CSSProperties } from 'react';
import { fmtKwh } from '../../lib/format';

interface Props {
  /** PV totaal vandaag tot nu (kWh) — _pv_total_today_kwh of pv_today_kwh */
  pvTotalKwh: number;
  /** Grid export vandaag (kWh) — site-totaal CT-meter; bevat ook battery→grid */
  pvExportKwh: number;
  /** Load totaal vandaag (household + backup, kWh) */
  totalLoadKwh: number | null;  // null = verbruik niet meetbaar (Erdotex)
  /** Directe PV→verbruik vandaag (kWh), per 5-min bucket geïntegreerd;
   *  null zolang de historie van vandaag ontbreekt. */
  pvDirectToLoadKwh: number | null;
}

export function EigenverbruikDuoTile({ pvTotalKwh, pvExportKwh, totalLoadKwh, pvDirectToLoadKwh }: Props) {
  // PV→batt telt hier niet als eigenverbruik: dat is "uitgesteld" en komt
  // pas 's avonds via batt→load terug, deels over middernacht heen.
  // Begrenzen op de dagtellers: bucket-integratie en teller lopen licht uiteen.
  const pvToLoadKwh =
    totalLoadKwh != null && pvDirectToLoadKwh != null && Number.isFinite(pvDirectToLoadKwh)
      ? Math.max(0, Math.min(pvDirectToLoadKwh, pvTotalKwh, totalLoadKwh))
      : null;

  // Surplus = PV die NIET direct naar load ging. Kan naar batterij of grid.
  const pvSurplusKwh = pvToLoadKwh != null ? Math.max(0, pvTotalKwh - pvToLoadKwh) : 0;

  // Approximate split: hoeveel van het surplus dachten we te exporteren?
  // Als grid_export > 0 EN surplus > 0 → toon "verkocht" als min(export, surplus).
  // Daarboven (export - surplus) komt uit batterij-discharge → niet PV-surplus.
  const pvSoldKwh = Math.min(pvSurplusKwh, Math.max(0, pvExportKwh));

  // Ratio's. Null als de noemer 0 is of de directe teller ontbreekt.
  const dekkingPct =
    pvToLoadKwh != null && totalLoadKwh != null && totalLoadKwh > 0
      ? Math.min(100, (pvToLoadKwh / totalLoadKwh) * 100)
      : null;
  const zelfverbruikPct =
    pvToLoadKwh != null && pvTotalKwh > 0
      ? Math.min(100, (pvToLoadKwh / pvTotalKwh) * 100)
      : null;

  const noData = pvTotalKwh <= 0 && (totalLoadKwh ?? 0) <= 0;

  return (
    <div
      className="kpi-tile eigenverbruik-duo-tile"
      style={{ ['--accent' as string]: '#f59e0b' } as CSSProperties}
      title={[
        `PV totaal vandaag: ${fmtKwh(pvTotalKwh)}`,
        `PV → direct verbruikt: ${pvToLoadKwh != null ? fmtKwh(pvToLoadKwh) : '—'}`,
        `PV surplus (naar batt of grid): ${fmtKwh(pvSurplusKwh)}`,
        `Verbruik totaal: ${totalLoadKwh != null ? fmtKwh(totalLoadKwh) : '— (niet meetbaar)'}`,
        `Grid export vandaag (incl. batt): ${fmtKwh(pvExportKwh)}`,
        '',
        'Dekking: hoeveel van het verbruik kwam uit eigen PV vandaag.',
        'Zelfverbruik: welk deel van de PV is direct verbruikt.',
        'Surplus: deel van de PV dat naar batterij of grid ging.',
      ].join('\n')}
    >
      <div className="kpi-label">Eigenverbruik PV</div>
      {noData ? (
        <div className="kpi-value small">geen PV vandaag</div>
      ) : (
        <div className="eigenverbruik-duo-grid">
          <div className="eigenverbruik-half">
            <div className="eigenverbruik-half-label">PV-dekking</div>
            <div className="eigenverbruik-half-value">
              {dekkingPct != null ? `${dekkingPct.toFixed(0)}%` : '—'}
            </div>
            <div
              className="eigenverbruik-bar"
              role="presentation"
              aria-label="PV-dekking van verbruik"
            >
              <div
                className="eigenverbruik-bar-fill"
                style={{ width: `${dekkingPct ?? 0}%`, background: '#22c55e' }}
              />
            </div>
            <div className="eigenverbruik-half-sub">
              {`${pvToLoadKwh != null ? fmtKwh(pvToLoadKwh) : '—'} van ${totalLoadKwh != null ? fmtKwh(totalLoadKwh) : '—'} verbruik`}
            </div>
          </div>
          <div className="eigenverbruik-half">
            <div className="eigenverbruik-half-label">PV-zelfverbruik</div>
            <div className="eigenverbruik-half-value">
              {zelfverbruikPct != null ? `${zelfverbruikPct.toFixed(0)}%` : '—'}
            </div>
            <div
              className="eigenverbruik-bar"
              role="presentation"
              aria-label="PV blijft in huis"
            >
              <div
                className="eigenverbruik-bar-fill"
                style={{ width: `${zelfverbruikPct ?? 0}%`, background: '#f59e0b' }}
              />
            </div>
            <div className="eigenverbruik-half-sub">
              {pvToLoadKwh == null
                ? '—'
                : pvSurplusKwh > 0
                ? `${fmtKwh(pvSurplusKwh)} surplus${pvSoldKwh > 0 ? ` (${fmtKwh(pvSoldKwh)} verkocht)` : ''}`
                : `geen surplus`}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
